///
/// EarthBrowser Admin Dashboard - Login Page
///
/// Created: 2025-11-27
/// Copyright (c) 2025 Geodata Labs
///

import { useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { Shield, ExternalLink, Loader2, KeyRound } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { toast } from '@/components/ui/use-toast'
import {
  useGetProfileQuery,
  useGetMyPermissionsQuery,
  useLazyGetPasskeyAuthOptionsQuery,
  useAuthenticateWithPasskeyMutation,
} from '@/redux/api/userApi'
import { useAppSelector } from '@/redux/store'
import { isWebAuthnSupported, authenticateWithPasskey } from '@/utils/passkey'

const API_URL = import.meta.env.VITE_API_URL || 'https://earthbrowser.com'

export function LoginPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const userProfile = useAppSelector(state => state.user.userProfile)
  const isAdmin = useAppSelector(state => state.user.isAdmin)
  const [isPasskeyLoading, setIsPasskeyLoading] = useState(false)
  const [isGoogleLoading, setIsGoogleLoading] = useState(false)

  // Check if already authenticated
  const { isLoading: profileLoading } = useGetProfileQuery()
  const { isLoading: permissionsLoading, refetch: refetchPermissions } = useGetMyPermissionsQuery(undefined, {
    skip: !userProfile,
  })

  // Passkey hooks
  const [getPasskeyAuthOptions] = useLazyGetPasskeyAuthOptionsQuery()
  const [authenticatePasskey] = useAuthenticateWithPasskeyMutation()

  const isLoading = profileLoading || permissionsLoading
  const isPasskeyAvailable = isWebAuthnSupported()

  // Redirect if already logged in as admin, keeping any deep-link params
  useEffect(() => {
    if (userProfile && isAdmin) {
      navigate({ pathname: '/', search: location.search })
    }
  }, [userProfile, isAdmin, navigate, location.search])

  const handleGoogleLogin = async () => {
    setIsGoogleLoading(true)
    try {
      // Fetch the Google authorization URL, asking the server to redirect
      // back to the admin console after signin
      const returnUrl = encodeURIComponent(window.location.origin)
      const response = await fetch(`${API_URL}/api/webhooks/google-oauth-url?flow=signin&redirect=${returnUrl}`, {
        credentials: 'include',
      })

      if (!response.ok) {
        throw new Error(`Server returned ${response.status}`)
      }

      const data = await response.json() as { url: string }
      window.location.href = data.url
    } catch (error) {
      console.error('Google login error:', error)
      toast({
        title: 'Sign In Failed',
        description: error instanceof Error ? error.message : 'Failed to start Google sign in',
        variant: 'destructive',
      })
      setIsGoogleLoading(false)
    }
  }

  const handlePasskeyLogin = async () => {
    setIsPasskeyLoading(true)
    try {
      // Get authentication options from server
      const optionsResult = await getPasskeyAuthOptions()

      if (!optionsResult.data) {
        throw new Error("Failed to get authentication options from server")
      }

      // Perform WebAuthn authentication
      const credential = await authenticateWithPasskey(optionsResult.data)

      // Send credential to server for verification
      const result = await authenticatePasskey({ credential })

      if (result.data) {
        // Refetch permissions after successful login
        await refetchPermissions()

        toast({
          title: "Signed in successfully",
          description: "Welcome to the admin dashboard.",
        })

        // Navigation will happen automatically via the useEffect
      } else if (result.error) {
        const errorMessage =
          'data' in result.error && result.error.data && typeof result.error.data === 'object' && 'message' in result.error.data
            ? String(result.error.data.message)
            : "Authentication failed"
        throw new Error(errorMessage)
      }
    } catch (error) {
      console.error("Passkey login error:", error)
      toast({
        title: "Authentication Failed",
        description: error instanceof Error ? error.message : "Failed to authenticate with passkey",
        variant: "destructive",
      })
    } finally {
      setIsPasskeyLoading(false)
    }
  }

  const handleMainSiteLogin = () => {
    window.open(`${API_URL}`, '_blank')
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-muted-foreground">Checking authentication...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-blue-950 to-slate-950 flex items-center justify-center p-4">
      <Card className="w-full max-w-md bg-card/95 backdrop-blur border-border rounded-3xl transition-all duration-300 hover:shadow-2xl">
        <CardHeader className="text-center space-y-4">
          <div className="mx-auto relative w-16 h-16">
            <div className="absolute inset-0 bg-gradient-to-br from-blue-600 via-cyan-500 to-teal-500 rounded-full blur-md opacity-75" />
            <div className="relative w-16 h-16 bg-blue-500/20 rounded-full flex items-center justify-center">
              <Shield className="h-8 w-8 text-blue-600 dark:text-blue-400" />
            </div>
          </div>
          <div>
            <CardTitle className="text-2xl font-bold bg-gradient-to-r from-blue-600 via-cyan-600 to-teal-600 bg-clip-text text-transparent">
              EarthBrowser Admin
            </CardTitle>
            <CardDescription className="text-base mt-2">
              Sign in to the admin console
            </CardDescription>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {userProfile && !isAdmin ? (
            <div className="text-center space-y-4">
              <div className="p-4 bg-destructive/10 rounded-xl border border-destructive/20">
                <p className="text-sm text-destructive">
                  You are signed in as <strong>{userProfile.email}</strong>, but you do not have admin privileges.
                </p>
              </div>
              <p className="text-sm text-muted-foreground">
                Please contact a system administrator if you believe you should have access.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Passkey Login - Primary method */}
              {isPasskeyAvailable && (
                <Button
                  onClick={handlePasskeyLogin}
                  className="w-full rounded-full transition-all duration-300 hover:scale-105 hover:shadow-xl bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white focus-visible:ring-2 focus-visible:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
                  size="lg"
                  disabled={isPasskeyLoading}
                >
                  {isPasskeyLoading ? (
                    <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                  ) : (
                    <KeyRound className="h-5 w-5 mr-2" />
                  )}
                  Sign in with Passkey
                </Button>
              )}

              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t border-border" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-card px-2 text-muted-foreground">Or continue with</span>
                </div>
              </div>

              <Button
                onClick={handleGoogleLogin}
                variant="outline"
                className="w-full rounded-full transition-all duration-300 hover:scale-105 focus-visible:ring-2 focus-visible:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
                size="lg"
                disabled={isGoogleLoading}
              >
                {isGoogleLoading ? (
                  <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                ) : (
                <svg className="h-5 w-5 mr-2" viewBox="0 0 24 24">
                  <path
                    fill="currentColor"
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  />
                  <path
                    fill="currentColor"
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  />
                  <path
                    fill="currentColor"
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  />
                  <path
                    fill="currentColor"
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  />
                </svg>
                )}
                Google
              </Button>

              <Button
                onClick={handleMainSiteLogin}
                variant="ghost"
                className="w-full rounded-full text-muted-foreground hover:text-foreground transition-all duration-300 focus-visible:ring-2 focus-visible:ring-blue-500"
                size="sm"
              >
                <ExternalLink className="h-4 w-4 mr-2" />
                Sign in on EarthBrowser.com
              </Button>
            </div>
          )}

          <p className="text-xs text-center text-muted-foreground">
            Access is restricted to authorized administrators only.
          </p>
        </CardContent>
      </Card>
    </div>
  )
}

